import { BusinessBasic, WorkingHour, BookingType, Staff, BusinessData, WorkingHourGroup } from "../../types"; 
import {
  AddBusinessAction, AddBusinessesAction, updateBusinessAction,
  UpdateBusinessWorkingHourAction, UpdateStaffAction, DeleteStaffAction,
  UpdateBusinessBookingTypeAction, deleteBookingTypeAction, SetSelectedBusinessAction,
  BusinessActionTypes,
  AddStaffAction, 
  UpdateBookingTypeAction,
  UpdateStaffWorkingHoursAction,
  WorkingHourOperation,
  UpdateWorkingHourGroupsAction, 
} from "../actions/businessActions";
 
// Reducer
export interface BusinessState {
  businesses: BusinessData[];
  selectedBusiness: BusinessData | null;
  source: "MY_BUSINESSES" | "ALL_BUSINESSES" | null;
}

export type BusinessAction =
  | AddBusinessAction
  | AddBusinessesAction
  | updateBusinessAction
  | UpdateBusinessWorkingHourAction
  | AddStaffAction
  | UpdateStaffWorkingHoursAction
  | UpdateStaffAction
  | DeleteStaffAction
  | UpdateBusinessBookingTypeAction
  | deleteBookingTypeAction
  | UpdateBookingTypeAction
  | SetSelectedBusinessAction
  | UpdateWorkingHourGroupsAction;

const initialState: BusinessState = {
  businesses: [] as BusinessData[],
  selectedBusiness: null as BusinessData | null,
  source: null,
};

type UpdateFunction = (
  business: BusinessData,
  action: BusinessAction
) => BusinessData;

const updateOrAddBusinessInList = (
  businesses: BusinessData[],
  businessId: number,
  action: BusinessAction,
  updateFunction: UpdateFunction
): { businesses: BusinessData[]; selectedBusiness: BusinessData | null } => {
  let updatedBusinesses: BusinessData[] = [...businesses]; // Initialize with a copy
  let selectedBusiness: BusinessData | null = null;

  const indexToUpdate = businesses.findIndex(
    (business) => Number(business.businessBasic.id) === Number(businessId)
  );

  if (indexToUpdate !== -1) {
    const updatedBusiness = updateFunction(businesses[indexToUpdate], action);
    selectedBusiness = updatedBusiness;
    updatedBusinesses[indexToUpdate] = updatedBusiness;
  }

  return { businesses: updatedBusinesses, selectedBusiness };
};

const businessReducer = (
  state: BusinessState = initialState,
  action: BusinessAction
): BusinessState => {
  switch (action.type) {
    case BusinessActionTypes.ADD_BUSINESS: {
      const business = action.payload.businessData;

      const existingBusinessIndex = state.businesses.findIndex(
        (c) => c.businessBasic.id === business.businessBasic.id
      );

      if (existingBusinessIndex !== -1) {
        const updatedBusinesses = [...state.businesses];
        updatedBusinesses[existingBusinessIndex] = business;

        return {
          ...state,
          businesses: updatedBusinesses,
          selectedBusiness:
            state.selectedBusiness?.businessBasic.id ===
            business.businessBasic.id
              ? business
              : state.selectedBusiness,
        };
      } else {
        return {
          ...state,
          businesses: [...state.businesses, business],
        };
      }
    }
    case BusinessActionTypes.ADD_BUSINESSES: {
      const payload = action.payload as {
        businesses: BusinessData[];
        source: "MY_BUSINESSES" | "ALL_BUSINESSES";
      };

      const newBusinesses = payload.businesses;
      const source = payload.source;

      let selectedBusiness: BusinessData | null = null;

      if (state.selectedBusiness) {
        const matchingBusiness = newBusinesses.find(
          (c) => c.businessBasic.id === state.selectedBusiness?.businessBasic.id
        );
        selectedBusiness = matchingBusiness ?? null;
      }

      return {
        ...state,
        businesses: newBusinesses,
        selectedBusiness,
        source,
      };
    }

    case BusinessActionTypes.UPDATE_BUSINESS_BASIC: {
      const payload = action.payload as {
        businessId: number;
        businessBasic: BusinessBasic;
      };
      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        (business: BusinessData, action: BusinessAction) => ({
          ...business,
          businessBasic: payload.businessBasic,
        })
      );

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: result.selectedBusiness,
      };
    }

    case BusinessActionTypes.UPDATE_STAFF: {
      const payload = action.payload as { businessId: number; staff: Staff };
      const updateBusinessStaff = (business: BusinessData) => {
        const updatedStaffs = [...business.businessStaffs];

        const index = updatedStaffs.findIndex(
          (s) => Number(s.id) === Number(payload.staff.id)
        );

        if (index !== -1) {
          updatedStaffs[index] = payload.staff;
        } else {
          updatedStaffs.push(payload.staff);
        }

        return {
          ...business,
          businessStaffs: updatedStaffs,
        };
      };

      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        updateBusinessStaff
      );

      const updatedSelectedBusiness =
        state.selectedBusiness &&
        Number(state.selectedBusiness.businessBasic.id) ===
          Number(payload.businessId)
          ? updateBusinessStaff(state.selectedBusiness)
          : result.selectedBusiness;

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: updatedSelectedBusiness ?? state.selectedBusiness,
      };
    }

    case BusinessActionTypes.DELETE_STAFF: {
      const payload = action.payload as { businessId: number; staffId: number };
      const removeBusinessStaff = (business: BusinessData) => ({
        ...business,
        businessStaffs: business.businessStaffs.filter(
          (staff) => staff.id !== payload.staffId
        ),
      });

      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        removeBusinessStaff
      );

      const updatedSelectedBusiness =
        state.selectedBusiness &&
        Number(state.selectedBusiness.businessBasic.id) ===
          Number(payload.businessId)
          ? removeBusinessStaff(state.selectedBusiness)
          : result.selectedBusiness;

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: updatedSelectedBusiness ?? state.selectedBusiness,
      };
    }
    case BusinessActionTypes.UPDATE_STAFF_WORKING_HOUR: {
      const payload = action.payload as {
        businessId: number;
        staffId: number;
        updatedWorkingHours: WorkingHour[]; // Array for multiple records
        workingHourGroup: WorkingHourGroup; // Full group object to use if creating a new group
        operation: WorkingHourOperation;
      };

      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        (business: BusinessData, action: BusinessAction) => {
          const updatedStaffs = business.businessStaffs.map((staff) => {
            if (Number(staff.id) === Number(payload.staffId)) {
              // Find the working hour group for this staff member
              let staffWorkingHourGroup = staff.workingHourGroups?.find(
                (group) =>
                  Number(group.id) ===
                  Number(payload.updatedWorkingHours[0].groupId)
              );

              // If the group doesn’t exist, use the provided workingHourGroup directly
              if (!staffWorkingHourGroup) {
                staffWorkingHourGroup = {
                  ...payload.workingHourGroup,
                  workingHours: [...payload.updatedWorkingHours], // Initialize with new hours
                };

                return {
                  ...staff,
                  workingHourGroups: [
                    ...(staff.workingHourGroups || []),
                    staffWorkingHourGroup,
                  ],
                };
              }

              // Modify existing working hours based on the operation
              let updatedWorkingHours: WorkingHour[] = [];
              switch (payload.operation) {
                case WorkingHourOperation.ADD:
                  updatedWorkingHours = [
                    ...staffWorkingHourGroup.workingHours,
                    ...payload.updatedWorkingHours,
                  ];
                  break;

                case WorkingHourOperation.UPDATE:
                  updatedWorkingHours = staffWorkingHourGroup.workingHours.map(
                    (staffHour) =>
                      payload.updatedWorkingHours.find(
                        (updatedHour) => updatedHour.id === staffHour.id
                      )
                        ? {
                            ...staffHour,
                            ...payload.updatedWorkingHours.find(
                              (updatedHour) => updatedHour.id === staffHour.id
                            ),
                          }
                        : staffHour
                  );
                  break;

                case WorkingHourOperation.DELETE:
                  updatedWorkingHours =
                    staffWorkingHourGroup.workingHours.filter(
                      (staffHour) =>
                        !payload.updatedWorkingHours.some(
                          (updatedHour) => updatedHour.id === staffHour.id
                        )
                    );
                  break;

                default:
                  return staff; // No operation to apply
              }

              const updatedWorkingHourGroups = staff?.workingHourGroups?.map(
                (group) =>
                  group.id === staffWorkingHourGroup?.id
                    ? { ...group, workingHours: updatedWorkingHours }
                    : group
              );

              return {
                ...staff,
                workingHourGroups: updatedWorkingHourGroups,
              };
            }
            return staff;
          });

          return {
            ...business,
            businessStaffs: updatedStaffs,
          };
        }
      );

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: result.selectedBusiness,
      };
    }

    case BusinessActionTypes.UPDATE_BUSINESS_WORKING_HOUR: {
      const payload = action.payload as {
        businessId: number;
        workingHour: WorkingHour;
        operation: WorkingHourOperation;
      };

      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        (business: BusinessData, action: BusinessAction) => {
          const businessWorkingHourGroup = business.workingHourGroups.find(
            (group) => Number(group.id) === Number(payload.workingHour.groupId)
          );

          if (!businessWorkingHourGroup) {
            return business; // If no matching group, return the business unchanged
          }

          let updatedWorkingHours: WorkingHour[];

          switch (payload.operation) {
            case WorkingHourOperation.ADD:
              // Add the new working hour
              updatedWorkingHours = [
                ...businessWorkingHourGroup.workingHours,
                payload.workingHour,
              ];
              break;

            case WorkingHourOperation.UPDATE:
              // Update the existing working hour
              updatedWorkingHours = businessWorkingHourGroup.workingHours.map(
                (businessHour) =>
                  businessHour.id === payload.workingHour.id
                    ? { ...businessHour, ...payload.workingHour }
                    : businessHour
              );
              break;

            case WorkingHourOperation.DELETE:
              // Remove the working hour
              updatedWorkingHours =
                businessWorkingHourGroup.workingHours.filter(
                  (businessHour) => businessHour.id !== payload.workingHour.id
                );
              break;

            default:
              return business; // No valid operation provided
          }

          const updatedWorkingHourGroups = business.workingHourGroups.map(
            (group) => {
              if (Number(group.id) === Number(payload.workingHour.groupId)) {
                return {
                  ...group,
                  workingHours: updatedWorkingHours, // Replace working hours for the matched group
                };
              }
              return group;
            }
          );

          return {
            ...business,
            workingHourGroups: updatedWorkingHourGroups,
          };
        }
      );

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: result.selectedBusiness,
      };
    }

    case BusinessActionTypes.UPDATE_WORKING_GROUP: {
      const payload = action.payload as {
        staffId?: number; // Make staffId optional
        businessId: number;
        workingHourGroup: WorkingHourGroup;
        operation: WorkingHourOperation;
      };

      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        (business: BusinessData, action: BusinessAction) => {
          if (payload.staffId) {
            // If staffId is provided, update the workingHourGroup for that staff member
            const updatedStaffMembers = business.businessStaffs.map((staff) => {
              if (staff.id === payload.staffId) {
                const updatedWorkingHourGroups = staff.workingHourGroups?.map(
                  (group) =>
                    group.id === payload.workingHourGroup?.id
                      ? { ...group, ...payload.workingHourGroup } // Update the group
                      : group
                );

                return {
                  ...staff,
                  workingHourGroups: updatedWorkingHourGroups,
                };
              }
              return staff;
            });

            return {
              ...business,
              businessStaffs: updatedStaffMembers,
            };
          } else {
            // If staffId is not provided, update the groupId for the business
            const updatedWorkingHourGroups = business.workingHourGroups.map(
              (group) =>
                group.id === payload.workingHourGroup.id
                  ? { ...group, ...payload.workingHourGroup } // Update the group
                  : group
            );

            return {
              ...business,
              workingHourGroups: updatedWorkingHourGroups,
            };
          }
        }
      );

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: result.selectedBusiness,
      };
    }

    case BusinessActionTypes.UPDATE_BUSINESS_SERVICE: {
      const payload = action.payload as {
        businessId: number;
        businessBookingType: BookingType;
      };
      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        (business: BusinessData, action: BusinessAction) => {
          const updatedbusinessBookingTypes = [
            ...business.businessBookingTypes,
          ];

          const index = updatedbusinessBookingTypes.findIndex(
            (s) => s.id === payload.businessBookingType.id
          );

          if (index !== -1) {
            updatedbusinessBookingTypes[index] = payload.businessBookingType;
          } else {
            updatedbusinessBookingTypes.push(payload.businessBookingType);
          }

          return {
            ...business,
            businessBookingTypes: updatedbusinessBookingTypes,
          };
        }
      );

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: result.selectedBusiness,
      };
    }

    case BusinessActionTypes.DELETE_BUSINESS_SERVICE: {
      const payload = action.payload as {
        businessId: number;
        bookingTypeId: number;
      };
      const result = updateOrAddBusinessInList(
        state.businesses,
        payload.businessId,
        action,
        (business: BusinessData, action: BusinessAction) => ({
          ...business,
          businessBookingTypes: business.businessBookingTypes.filter(
            (bookingType) => Number(bookingType.id) !== payload.bookingTypeId
          ),
        })
      );

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: result.selectedBusiness,
      };
    }

    case BusinessActionTypes.UPDATE_BOOKING_TYPE: {
      const payload = action.payload as {
        businessId: number;
        bookingType: BookingType;
        assignedStaffs: Staff[];
      };
      const { businessId, bookingType, assignedStaffs } = payload;

      const result = updateOrAddBusinessInList(
        state.businesses,
        businessId,
        action,
        (business: BusinessData, action: BusinessAction) => {
          let updatedBookingTypes = [...business.businessBookingTypes];
          const indexToUpdate = updatedBookingTypes.findIndex(
            (existingBookingType) => existingBookingType.id === bookingType.id
          );

          if (indexToUpdate !== -1) {
            updatedBookingTypes[indexToUpdate] = {
              ...updatedBookingTypes[indexToUpdate],
              ...bookingType,
              assignedStaffs: assignedStaffs,
            };
          } else {
            updatedBookingTypes.push({
              ...bookingType,
              assignedStaffs: assignedStaffs,
            });
          }

          return {
            ...business,
            businessBookingTypes: updatedBookingTypes,
          };
        }
      );

      return {
        ...state,
        businesses: result.businesses,
        selectedBusiness: result.selectedBusiness,
      };
    }

    case BusinessActionTypes.SET_SELECTED_BUSINESS: {
      const selectedBusinessId = action.payload;
      const selectedBusiness = selectedBusinessId
        ? state.businesses.find(
            (business) =>
              Number(business.businessBasic.id) === Number(selectedBusinessId)
          ) || null
        : null;

      return {
        ...state,
        selectedBusiness,
      };
    }
    default:
      return state;
  }
};
 
 

export default businessReducer;
